/* eslint-disable ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { sort, alias } from '@ember/object/computed';
import { action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import GradingMethod from 'linced-app/enums/grading-method';
import { getGradeForDisplay, getPossibleGrades } from 'linced-app/utils/grades';
import moment from 'moment';
import { service } from '@ember/service';
import { mapBy } from '@linc-technologies/linced-common/utils/arrays';

export default class AssessmentController extends Controller {
  @service
  dates;

  @service
  session;

  GradingMethod = GradingMethod;

  @alias('session.school')
  school;

  learnersSortOrder = Object.freeze(['firstName', 'lastName']);

  @sort('model.learners', 'learnersSortOrder')
  sortedLearners;

  @computed('model.{group.name,assessment.label}')
  get exportTitle() {
    return `${this.model.group.name} - Progress Snapshots - ${this.model.assessment.label}`;
  }

  @alias('model.learners.length')
  numLearners;

  @computed(
    'model.terms.[]',
    'dates.{previousAcademicYearLabel,currentAcademicYearLabel}',
  )
  get terms() {
    let terms = [];

    let lastYear = this.model.terms.filter(
      (item) => item.academicYear === this.dates.previousAcademicYearLabel,
    );
    let thisYear = this.model.terms.filter(
      (item) => item.academicYear === this.dates.currentAcademicYearLabel,
    );

    terms.push(...this._getStretchedTerms(lastYear));
    terms.push(...this._getStretchedTerms(thisYear));

    return terms;
  }

  @computed('model.modules.[]')
  get modules() {
    return this.model.modules
      .slice()
      .sort(
        (a, b) =>
          a.sequence - b.sequence ||
          (a.label || '').localeCompare(b.label || ''),
      );
  }

  @computed('sortedLearners', 'model.{modules.[],grades.[]}', 'terms.[]')
  get learners() {
    let learners = [];
    this.sortedLearners.forEach((learner) => {
      let learnerGrades = this.model.grades.filter(
        (item) => item.person_id === learner.id,
      );
      let grades = [];

      this.modules.forEach((module) => {
        let moduleGrades = learnerGrades.filter(
          (item) => item.module_id === module.id,
        );
        let previousGrade = null;

        this.terms.forEach((term) => {
          let termStart = moment(term.start);
          let termEnd = moment(term.end);
          let termGrades = moduleGrades.filter((grade) => {
            let gradeTime = moment(grade.createTime * 1000);
            return (
              gradeTime.isSameOrAfter(termStart, 'day') &&
              gradeTime.isSameOrBefore(termEnd, 'day')
            );
          });
          let grade = termGrades
            .slice()
            .sort((a, b) => a.createTime - b.createTime)
            .at(-1);
          if (!isEmpty(grade)) {
            let status = '';
            if (!isEmpty(previousGrade)) {
              // We have a previous grade to compare against.  Figure out whether they've moved, and in which direction.
              let previous = 0;
              let current = 0;
              let values = [];
              switch (grade.gradeType) {
                case GradingMethod.NUMERIC:
                  previous = previousGrade.awarded;
                  current = grade.awarded;
                  break;
                case GradingMethod.SELECT:
                case GradingMethod.ICON_SELECT:
                  values = mapBy(
                    module.get('gradeSchedule.select.values'),
                    'value',
                  );
                  previous = values.indexOf(previousGrade.awarded);
                  current = values.indexOf(grade.awarded);
                  break;
                case GradingMethod.MULTIPLE_SELECT:
                  previous = previousGrade.multiSelectAwarded.length;
                  current = grade.multiSelectAwarded.length;
                  break;
              }
              switch (true) {
                case current === previous:
                  status = 'progress-none';
                  break;
                case current < previous:
                  status = 'progress-down';
                  break;
                case current > previous:
                  status = 'progress-up';
                  break;
              }
            }

            grades.push({
              year: term.year,
              term: term.term,
              module_id: grade.module_id,
              grade: grade,
              person: grade.person,
              status,
            });

            previousGrade = grade;
          }
        });
      });

      learners.push({
        person: learner,
        person_id: learner.id,
        grades,
      });
    });

    return learners;
  }

  @computed('learners.[]', 'modules.[]', 'terms.[]')
  get exportData() {
    return this.learners.map((learner) => {
      let person = learner.person.getProperties([
        'id',
        'jurisdictionalId',
        'firstName',
        'lastName',
        'currentYearLevel',
        'gender',
        'jurisdictionalEthnicity',
      ]);
      let row = [
        person.jurisdictionalId,
        person.firstName,
        person.lastName,
        person.currentYearLevel,
        person.gender,
        person.jurisdictionalEthnicity,
      ];

      this.modules.forEach((module) => {
        let moduleGrades = learner.grades.filter(
          (item) => item.module_id === module.id,
        );
        this.terms.forEach((term) => {
          let termGrade = moduleGrades.find((grade) => {
            return grade.year === term.year && grade.term === term.term;
          });
          if (isEmpty(termGrade) || isEmpty(termGrade.grade)) {
            row.push('');
          } else {
            row.push(getGradeForDisplay(module, termGrade.grade, true));
          }
        });
      });

      return row;
    });
  }

  @computed('model.{learners.[],modules.[],grades.[]}', 'terms.[]')
  get gradesByTerm() {
    return this.learners.reduce((grades, learner) => {
      grades.push(...learner.grades);
      return grades;
    }, []);
  }

  termColors = Object.freeze([
    '#1565C0', // Blue 800
    '#D84315', // Deep Orange 800
    '#FF8F00', // Amber 800
    '#2E7D32', // Green 800
    '#6A1B9A', // Purple 800
    '#00ACC1', // Cyan 600
    '#E91E63', // Pink 500
    '#9E9D24', // Lime 800
  ]);

  @tracked
  detailData;

  @tracked
  detailDialogOpen;

  @tracked
  detailDirection;

  @tracked
  detailModule;

  @action
  getTermGrade(grades, term) {
    return grades.find((grade) => {
      return grade.year === term.year && grade.term === term.term;
    });
  }

  @action
  getPossibleGrades(module) {
    return getPossibleGrades(module);
  }

  @action
  getSummary(module) {
    let moduleGrades = this.gradesByTerm.filter(
      (item) => item.module_id === module.id,
    );
    let possibleGrades = getPossibleGrades(module);
    let terms = [];

    if (isEmpty(possibleGrades)) {
      // No grades possible - no schedule defined?
      return [];
    }

    this.terms.forEach((term) => {
      let termGrades = moduleGrades.filter((grade) => {
        return grade.year === term.year && grade.term === term.term;
      });
      let grades = [];
      possibleGrades.forEach((possible, idx) => {
        let matchingGrades = [];
        let rawGrade;
        let nextPossible = possible;
        if (idx < possibleGrades.length - 1) {
          nextPossible = possibleGrades[idx + 1];
        }
        switch (module.get('gradeSchedule.awardMethod')) {
          case GradingMethod.MULTIPLE_SELECT:
            matchingGrades = termGrades.filter((grade) => {
              return grade.grade
                .get('multiSelectAwarded')
                .includes(possible.value);
            });
            break;

          case GradingMethod.NUMERIC:
            matchingGrades = termGrades.filter((grade) => {
              rawGrade = grade.grade.get('numericAwarded');
              switch (idx) {
                case 0:
                  // Lowest possible grade.
                  // Include everything below the next grade.
                  return rawGrade < nextPossible.value;
                case possibleGrades.length - 1:
                  // Highest possible grade.  Include it and above.
                  return rawGrade >= possible.value;
                default:
                  // All grades in between.  Include it, and everything above
                  // it but below the next possible grade.
                  return (
                    possible.value <= rawGrade && rawGrade < nextPossible.value
                  );
              }
            });
            break;

          default:
            matchingGrades = termGrades.filter(
              (item) => item.grade.awarded === possible.value,
            );
            break;
        }

        if (!isEmpty(matchingGrades)) {
          grades.push({
            academicYear: term.academicYear,
            term: term.term,
            value: possible.value,
            label: possible.label,
            total: matchingGrades.length,
            up: matchingGrades.filter((item) => item.status === 'progress-up'),
            down: matchingGrades.filter(
              (item) => item.status === 'progress-down',
            ),
            same: matchingGrades.filter((record) => {
              // Include initial data points with actual non-movers.
              return (
                record.status === 'progress-none' || isEmpty(record.status)
              );
            }),
          });
        }
      });
      terms.push({
        academicYear: term.academicYear,
        term: term.term,
        grades,
      });
    });

    return terms;
  }

  @action
  getChartData(module, summary) {
    let possibleGrades = getPossibleGrades(module);
    let data = [];

    possibleGrades.forEach((grade) => {
      let grades = this.getDefaultChartData();
      summary.forEach((term) => {
        let count = term.grades.find((item) => item.value === grade.value);
        if (!isEmpty(count)) {
          grades[this.getTermLabel(term)] = count.total;
        }
      });
      data.push({
        value: grade.value,
        label: grade.label,
        data: grades,
      });
    });

    return data;
  }

  @action
  getPercentageOfLearners(num) {
    let result = 0;
    if (num <= 0 || this.numLearners <= 0) {
      return result;
    }

    result = (num / this.numLearners) * 100;
    return result.toFixed(2);
  }

  @action
  openDetailDialog(data, direction, module) {
    this.detailData = data;
    this.detailDirection = direction;
    this.detailModule = module;
    this.detailDialogOpen = true;
  }

  getTermLabel(term) {
    return `T${term.term} - ${term.academicYear}`;
  }

  getDefaultChartData() {
    let data = {};
    this.terms.forEach((term) => {
      data[this.getTermLabel(term)] = 0;
    });
    return data;
  }

  _getStretchedTerms(terms) {
    for (let i = 1; i <= terms.length; i++) {
      let term = terms.find((item) => item.term === i);
      let next = terms.find((item) => item.term === i + 1);
      if (isEmpty(term)) {
        continue;
      }
      if (i === 1) {
        term.set('start', this.dates.previousRolloverDate(term.start));
      }
      let nextStart;
      if (isEmpty(next)) {
        nextStart = this.dates.nextRolloverDate(term.end);
      } else {
        nextStart = next.start;
      }
      let endOfTerm = moment(nextStart)
        .subtract(1, 'days')
        .format('YYYY-MM-DD');
      term.set('end', endOfTerm);
    }

    return terms;
  }
}
